/**
 * `<SpecImportForm>` — import an API spec (OpenAPI/Swagger or Postman).
 *
 * Flow: paste or link a spec (+ optional auth header) → "Validate"
 * (`POST /api/api-specs/validate`, shows endpoint count + warnings, or the
 * readable error list) → "Import" (`POST /api/api-specs`) which generates one
 * `api_endpoint` skill per endpoint.
 *
 * Both endpoints are WP-4 and may be absent on this branch; a 404 surfaces as
 * an `AgentsApiError` and we show an unavailable hint rather than a stack trace.
 */

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AgentsApiError } from "../../agents/api/client";
import { formatApiError } from "../../lib/format-error";
import { StatusPill } from "../../components/status-pill";
import {
  useImportSpec,
  useValidateSpec,
  type SpecValidateRequest,
  type SpecValidateResult,
} from "../api/onboarding";

type Mode = "paste" | "url";

export function SpecImportForm({
  slug,
  open,
  onOpenChange,
}: {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("onboarding");

  const validate = useValidateSpec();
  const importSpec = useImportSpec(slug);

  const [mode, setMode] = useState<Mode>("paste");
  const [name, setName] = useState("");
  const [specText, setSpecText] = useState("");
  const [specUrl, setSpecUrl] = useState("");
  const [authName, setAuthName] = useState("");
  const [authValue, setAuthValue] = useState("");

  const [validated, setValidated] = useState<SpecValidateResult | null>(null);
  const [errors, setErrors] = useState<string[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  function reset() {
    setMode("paste");
    setName("");
    setSpecText("");
    setSpecUrl("");
    setAuthName("");
    setAuthValue("");
    setValidated(null);
    setErrors(null);
    setUnavailable(false);
  }

  const hasInput = mode === "paste" ? specText.trim() !== "" : specUrl.trim() !== "";

  function buildBody(): SpecValidateRequest {
    return {
      name: name.trim() || "API",
      specText: mode === "paste" ? specText : undefined,
      specUrl: mode === "url" ? specUrl.trim() : undefined,
      auth:
        authName.trim() && authValue
          ? { headerName: authName.trim(), headerValue: authValue }
          : undefined,
    };
  }

  function extractErrorList(err: unknown): string[] | null {
    if (err instanceof AgentsApiError) {
      const list = err.details?.errors;
      if (Array.isArray(list)) return list.map((e) => String(e));
    }
    return null;
  }

  function runValidate() {
    setValidated(null);
    setErrors(null);
    validate.mutate(buildBody(), {
      onSuccess: (res) => {
        setValidated(res);
        toast.success(t("spec.validOk", { count: res.endpoints.length }));
      },
      onError: (err) => {
        if (err instanceof AgentsApiError && err.status === 404) {
          setUnavailable(true);
          return;
        }
        const list = extractErrorList(err);
        if (list) setErrors(list);
        else toast.error(formatApiError(err));
      },
    });
  }

  function runImport() {
    importSpec.mutate(buildBody(), {
      onSuccess: (res) => {
        toast.success(t("spec.importedToast", { count: res.skillCount }));
        reset();
        onOpenChange(false);
      },
      onError: (err) => toast.error(formatApiError(err)),
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !importSpec.isPending && onOpenChange(o)}>
      <DialogContent data-testid="onboarding-spec-form">
        <DialogHeader>
          <DialogTitle>{t("spec.title")}</DialogTitle>
          <DialogDescription>{t("spec.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="spec-name">{t("spec.nameLabel")}</Label>
            <Input
              id="spec-name"
              value={name}
              placeholder={t("spec.namePlaceholder")}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <Tabs
            value={mode}
            onValueChange={(v) => {
              setMode(v as Mode);
              setValidated(null);
              setErrors(null);
            }}
          >
            <TabsList>
              <TabsTrigger value="paste">{t("spec.tabs.paste")}</TabsTrigger>
              <TabsTrigger value="url">{t("spec.tabs.url")}</TabsTrigger>
            </TabsList>
          </Tabs>

          {mode === "paste" ? (
            <div className="space-y-1">
              <Label htmlFor="spec-text">{t("spec.specTextLabel")}</Label>
              <Textarea
                id="spec-text"
                rows={6}
                value={specText}
                placeholder={t("spec.specTextPlaceholder")}
                onChange={(e) => {
                  setSpecText(e.target.value);
                  setValidated(null);
                }}
                data-testid="onboarding-spec-text"
              />
            </div>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="spec-url">{t("spec.specUrlLabel")}</Label>
              <Input
                id="spec-url"
                type="url"
                value={specUrl}
                placeholder={t("spec.specUrlPlaceholder")}
                onChange={(e) => {
                  setSpecUrl(e.target.value);
                  setValidated(null);
                }}
              />
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="spec-auth-name">{t("spec.authHeaderName")}</Label>
              <Input
                id="spec-auth-name"
                value={authName}
                placeholder={t("spec.authHeaderNamePlaceholder")}
                onChange={(e) => setAuthName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="spec-auth-value">{t("spec.authHeaderValue")}</Label>
              <Input
                id="spec-auth-value"
                type="password"
                value={authValue}
                onChange={(e) => setAuthValue(e.target.value)}
              />
            </div>
          </div>

          {unavailable ? (
            <p className="text-sm text-muted-foreground" data-testid="onboarding-spec-unavailable">
              {t("db.unavailable")}
            </p>
          ) : null}

          {validated ? (
            <div
              className="space-y-2 rounded-lg border border-border p-3"
              data-testid="onboarding-spec-preview"
            >
              <div className="flex items-center gap-2">
                <StatusPill tone="success">{t("status.connected")}</StatusPill>
                <span className="text-sm text-foreground">
                  {t("spec.validOk", { count: validated.endpoints.length })}
                </span>
              </div>
              {validated.warnings.length > 0 ? (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t("spec.warningsTitle")}
                  </p>
                  <ul className="list-inside list-disc text-xs text-muted-foreground">
                    {validated.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}

          {errors ? (
            <div
              className="space-y-1 rounded-lg border border-border p-3"
              data-testid="onboarding-spec-errors"
            >
              <div className="flex items-center gap-2">
                <StatusPill tone="danger">{t("status.error")}</StatusPill>
                <span className="text-sm font-medium text-foreground">{t("spec.errorsTitle")}</span>
              </div>
              <ul className="list-inside list-disc text-xs text-muted-foreground">
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={importSpec.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="outline"
            onClick={runValidate}
            disabled={!hasInput || validate.isPending}
            data-testid="onboarding-spec-validate"
          >
            {validate.isPending ? t("spec.validating") : t("spec.validate")}
          </Button>
          <Button
            onClick={runImport}
            disabled={!validated || importSpec.isPending}
            data-testid="onboarding-spec-import"
          >
            {importSpec.isPending
              ? t("spec.importing")
              : t("spec.import", { count: validated?.endpoints.length ?? 0 })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
